// Standard Library Imports
import React, { useEffect, useState, useContext } from 'react';
//External library imports
import { toast } from 'react-toastify';
import { UilImport } from '@iconscout/react-unicons';
import _ from 'lodash';
import moment from 'moment';
import { ArcherContainer, ArcherElement } from 'react-archer';

//Internal module imports
import { useLoader, useQueryParamWithDefault } from '../../../../../hooks';
import economicsClasses from '../../Economics.module.css';
import MonthPicker from '../../../../../components/Inputs/MonthPicker/MonthPicker';
import { DropdownComponent } from '../../../../../components/Inputs/Input';
import Table from '../../../../../components/Table/Table';
import { RootEconomicsService } from '../../../../../services/BiddingService/Root/EconomicsService';
import { dkCurrencyFormat } from '../../../../../utils/currencyHelper';
import { AuthContext } from '../../../../../context/AuthContext';
import { ROOT_USER_ROLES, ADMIN_USER_ROLES } from '../../../../../constants';
import { AdminEconomicsService } from '../../../../../services/BiddingService/Admin/EconomicsService';
import { DateParam } from 'use-query-params';
import IconComponent from '../../../../../components/Icon/Icon';
import { exportData } from '../../../../../utils/downloadHelper';

const Ledger = ({ cId, admins = [], customers = [], selectedAdmin, setSelectedAdmin, selectedCustomer, setSelectedCustomer }) => {
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useQueryParamWithDefault('month', DateParam, moment().startOf('month').toDate(), cId, { clearOnUnmount: false });
    const { state } = useContext(AuthContext);
    const userRole = _.get(state, ['user', 'userRole']);
    const isRootUser = ROOT_USER_ROLES.includes(userRole);
    const isAdminUser = ADMIN_USER_ROLES.includes(userRole);
    const [ledgerData, setLedgerData] = useState({
        tableData: [],
        overAllTotalRevenue: {
            total: 0,
            sc: 0,
            customer: 0,
        },
        rawData: [],
    });
    const [initialLoad, setInitialLoad] = useState(false);

    useEffect(() => {
        if (document.readyState === 'complete') setInitialLoad(true);
    }, []);

    useEffect(() => {
        if (selectedCustomer && selectedAdmin && date && initialLoad) getLedgerData();
    }, [selectedCustomer, selectedAdmin, initialLoad]);

    const getLedgerData = () => {
        const params = {
            month: moment(date).format('YYYY-MM'),
            customerId: selectedCustomer,
            ...(isRootUser ? { adminId: selectedAdmin } : {}),
        };

        let service;
        if (isRootUser) service = RootEconomicsService;
        if (isAdminUser) service = AdminEconomicsService;

        service.GetSCSavingLedgerData(params, startLoader, handleLedgerDataSuccess, handleError, stopLoader);
    };

    const handleLedgerDataSuccess = ({ data }) => {
        const processedData = _.get(data, ['data', 'data'], []).map((el) => ({
            partner: el.adminName,
            customer: el.customerName,
            formatedTotal: dkCurrencyFormat(el?.totalSaving || 0),
            formatedSc: dkCurrencyFormat(el?.totalOurSplit || 0),
            formatedCustomer: dkCurrencyFormat(el?.totalCustomerSplit || 0),
        }));

        setLedgerData({
            overAllTotalRevenue: {
                total: dkCurrencyFormat(_.get(data, ['data', 'overallTotal', 'total'], 0)),
                sc: dkCurrencyFormat(_.get(data, ['data', 'overallTotal', 'sc'], 0)),
                customer: dkCurrencyFormat(_.get(data, ['data', 'overallTotal', 'customer'], 0)),
            },
            tableData: processedData,
            rawData: {
                data: _.get(data, ['data', 'data'], []),
                overAllTotalRevenue: _.get(data, ['data', 'overallTotal'], {}),
            },
        });
    };

    const handleDownload = () => {
        const processedData = _.get(ledgerData, ['rawData', 'data']).map((el) => ({
            partner: el.adminName,
            customer: el.customerName,
            savings: el?.totalSaving || 0,
            scSavings: el?.totalOurSplit || 0,
            customerSavings: el?.totalCustomerSplit || 0,
        }));

        exportData(processedData, 'SavingData', ['partner', 'customer', 'savings', 'scSavings', 'customerSavings'], {
            partner: 'Total',
            customer: '',
            savings: _.get(ledgerData, ['rawData', 'overAllTotalRevenue', 'total'], 0),
            scSavings: _.get(ledgerData, ['rawData', 'overAllTotalRevenue', 'sc'], 0),
            customerSavings: _.get(ledgerData, ['rawData', 'overAllTotalRevenue', 'customer'], 0),
        });
    };

    const handleError = (err) => {
        toast.error(err?.response?.data?.message);
    };

    return (
        <div>
            <div className={economicsClasses.FilterBar}>
                <div className={economicsClasses.FilterContainer}>
                    {/* left container */}
                    <div className={economicsClasses.SubContainer}>
                        {isRootUser ? (
                            <>
                                <div className={economicsClasses.DropDownContainer}>
                                    <label>Admin</label>
                                    <DropdownComponent
                                        name="adminId"
                                        options={admins}
                                        defaultValue={{ value: selectedAdmin }}
                                        onChange={(e) => {
                                            if (e.value !== selectedAdmin) setSelectedAdmin(e.value);
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            ''
                        )}

                        <div className={economicsClasses.DropDownContainer}>
                            <label>Customer</label>
                            <DropdownComponent
                                name="customerId"
                                options={customers}
                                defaultValue={{ value: selectedCustomer }}
                                onChange={(e) => {
                                    if (e.value !== selectedCustomer) {
                                        setSelectedCustomer(e.value);
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* right container */}
                    <div className={economicsClasses.SubContainer}>
                        {date && (
                            <div className={economicsClasses.DropDownContainer}>
                                <label>Month</label>
                                <MonthPicker date={date} setDate={setDate} />
                            </div>
                        )}

                        <div style={{ marginTop: '0.8vh' }} onClick={() => getLedgerData()}>
                            <button className="btn-primary">Submit</button>
                        </div>

                        <div style={{ marginTop: '3vh' }} onClick={() => handleDownload()}>
                            <IconComponent icon={UilImport} tooltip={'Download'} size={'1.5vw'} />
                        </div>
                    </div>
                </div>
            </div>

            <div className={economicsClasses.TableContainer}>
                <div className={economicsClasses.LedgerTreeContainer}>
                    <ArcherContainer
                        endShape={{
                            arrow: {
                                arrowLength: 0,
                                arrowThickness: 0,
                            },
                        }}
                    >
                        <div className={economicsClasses.TreeContainer} style={{ justifyContent: 'center' }}>
                            <div className={economicsClasses.OverAllWrapper}>
                                <div className={economicsClasses.Center}>
                                    <ArcherElement
                                        id="start"
                                        relations={[
                                            {
                                                targetId: 'center',
                                                targetAnchor: 'top',
                                                sourceAnchor: 'bottom',
                                                style: {
                                                    strokeColor: 'var(--color-primary)',
                                                    strokeWidth: 2,
                                                },
                                            },
                                        ]}
                                    >
                                        <div className={economicsClasses.RevenueCardTotal}>
                                            <p>Total</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'total'])} kr.</span>
                                        </div>
                                    </ArcherElement>
                                </div>

                                <ArcherElement
                                    id={'center'}
                                    relations={[
                                        {
                                            targetId: 'sc',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },

                                        {
                                            targetId: 'customer',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                    ]}
                                >
                                    <div className={economicsClasses.Center}></div>
                                </ArcherElement>
                                <div className={economicsClasses.OverRevContainer} style={{ gap: '5vw' }}>
                                    <ArcherElement id={'sc'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>S.C.</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'sc'])} kr.</span>
                                        </div>
                                    </ArcherElement>

                                    <ArcherElement id={'customer'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>Customer </p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'customer'])} kr.</span>
                                        </div>
                                    </ArcherElement>
                                </div>
                            </div>
                        </div>
                    </ArcherContainer>
                </div>
                <Table
                    data={ledgerData.tableData}
                    head={[isRootUser && 'Partner', 'Customer', 'SC (kr.)', 'Customer (kr.)', 'Total (kr.)']}
                    keys={[isRootUser && 'partner', 'customer', 'formatedSc', 'formatedCustomer', 'formatedTotal']}
                />
            </div>
        </div>
    );
};

export default Ledger;
